import { logger } from '@zep/core-logger';

import { useQuizListItemProvider } from '@/shared/quizList/hooks/QuizListItemContext';
import { type QuizCardMenuItem } from '@/shared/quizList/hooks/useGetQuizListItemButtons';
import { type QuizListType } from '@/shared/quizList/QuizListType';
/**
 * 이름만 다르고 집계상 같은 행동인 액션.
 *
 * `save_to_my_quiz` 는 결과가 복사다. 새 이벤트를 만들면 `copy_quiz_folder` 의 추이가 끊긴다.
 */
const TRACK_ALIAS: Partial<
  Record<QuizCardMenuItem['onClickEventName'], EventType>
> = {
  save_to_my_quiz: 'copy',
};

export const useQuizCardEventTrack = () => {
  const { eventLocation, quizSet, eventAdditionalProperty } =
    useQuizListItemProvider();

  const tranEvent = (event: EventType) => {
    const eventName = eventActions[event][eventLocation];
    const additionalProperty = eventAdditionalProperty
      ? eventAdditionalProperty?.[eventLocation]
      : {};

    logger.track(eventName, {
      location: eventLocation,
      id: quizSet.id,
      ...additionalProperty,
    });
  };

  return {
    trackEnter: () => tranEvent('enter'),
    /*
      아직 이벤트가 정의되지 않은 액션(예: 폴더로 이동)은 조용히 건너뛴다 —
      이름·프로퍼티는 `docs/event-tracking` 규칙에 따라 따로 등록한다.
    */
    trackButtons: (type: QuizCardMenuItem['onClickEventName']) => {
      const event = TRACK_ALIAS[type] ?? type;
      if (isTrackedEvent(event)) tranEvent(event);
    },
  };
};

type LocationType = QuizListType;
type EventType =
  | 'enter'
  | 'copy'
  | 'share'
  | 'report'
  | 'update'
  | 'delete'
  | 'google_assignment'
  | 'lti_select';

/**
 * 위치별 이벤트명. 모든 위치가 모든 행동의 이름을 갖는다.
 *
 * `folder` 는 폴더 상세의 행이다. 남의 폴더 행은 입장·`내 퀴즈로 저장`(copy) 뿐이지만, 내 폴더 행은
 * 내 퀴즈 메뉴(공유·리포트·수정·삭제·과제 연동)를 그대로 쓴다. 이름이 빠진 조합은 이벤트가 사라진다.
 */
type EventActions = Record<EventType, Record<LocationType, string>>;

const isTrackedEvent = (value: string): value is EventType =>
  value in eventActions;

export const eventActions: EventActions = {
  enter: {
    myQuiz: 'enter_detail_myquiz',
    public: 'enter_detail_public',
    featured: 'enter_detail_featured',
    user: 'enter_detail_user',
    folder: 'enter_detail_folder',
  },
  copy: {
    myQuiz: 'copy_quiz_myquiz',
    public: 'copy_quiz_public',
    featured: 'copy_quiz_featured',
    user: 'copy_quiz_user',
    folder: 'copy_quiz_folder',
  },
  share: {
    myQuiz: 'share_myquiz',
    public: 'share_public',
    featured: 'share_featured',
    user: 'share_user',
    folder: 'share_folder',
  },
  report: {
    myQuiz: 'lms_myquiz',
    public: 'lms_public',
    featured: 'lms_featured',
    user: 'lms_user',
    folder: 'lms_folder',
  },
  delete: {
    myQuiz: 'delete_myquiz',
    public: 'delete_public',
    featured: 'delete_featured',
    user: 'delete_user',
    folder: 'delete_folder',
  },
  update: {
    myQuiz: 'update_myquiz',
    public: 'update_public',
    featured: 'update_featured',
    user: 'update_user',
    folder: 'update_folder',
  },
  google_assignment: {
    myQuiz: 'google_assignment_myquiz',
    public: 'google_assignment_public',
    featured: 'google_assignment_featured',
    user: 'google_assignment_user',
    folder: 'google_assignment_folder',
  },
  lti_select: {
    myQuiz: 'lti_select_myquiz',
    public: 'lti_select_public',
    featured: 'lti_select_featured',
    user: 'lti_select_user',
    folder: 'lti_select_folder',
  },
};
